import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Achievement } from '../../types/goals';
import {
  getUserAchievements,
  getRecentAchievements,
  getAchievementsByCategory,
  getAchievementStats,
  markAchievementViewed
} from '../../services/firebase/achievement.service';
import { 
  checkAchievements 
} from '../../services/firebase/achievementTriggers.service';

export interface AchievementsState {
  achievements: Achievement[];
  recentAchievements: Achievement[];
  newAchievements: Achievement[];
  statistics: {
    total: number;
    byCategory: Record<string, number>;
    recentlyEarned: Achievement[];
    mostValuable: Achievement[];
  } | null;
  currentCategory: string | null;
  isLoading: boolean;
  error: string | null;
  lastChecked: number | null;
  notificationQueue: Achievement[];
}

const initialState: AchievementsState = {
  achievements: [],
  recentAchievements: [],
  newAchievements: [],
  statistics: null,
  currentCategory: null,
  isLoading: false,
  error: null,
  lastChecked: null,
  notificationQueue: []
};

// Async thunks
export const fetchUserAchievements = createAsyncThunk(
  'achievements/fetchUser',
  async () => {
    const achievements = await getUserAchievements();
    return achievements;
  }
);

export const fetchRecentAchievements = createAsyncThunk(
  'achievements/fetchRecent',
  async (count: number = 5) => {
    const achievements = await getRecentAchievements(count);
    return achievements;
  }
);

export const fetchAchievementsByCategory = createAsyncThunk(
  'achievements/fetchByCategory',
  async (category: string) => {
    const achievements = await getAchievementsByCategory(category);
    return { category, achievements };
  }
);

export const fetchAchievementStats = createAsyncThunk(
  'achievements/fetchStats',
  async () => {
    const stats = await getAchievementStats();
    return stats;
  }
);

export const markAchievementAsViewed = createAsyncThunk(
  'achievements/markViewed',
  async (achievementId: string) => {
    await markAchievementViewed(achievementId);
    return achievementId;
  }
);

export const checkForNewAchievements = createAsyncThunk(
  'achievements/check',
  async (params: {
    action: 'training_added' | 'competition_added' | 'goal_created' | 'goal_completed' | 'profile_updated' | 'app_opened';
    data?: any;
  }) => {
    const achievements = await checkAchievements(params.action, params.data);
    return achievements;
  }
);

const achievementsSlice = createSlice({
  name: 'achievements',
  initialState,
  reducers: {
    setCurrentCategory: (state, action: PayloadAction<string | null>) => {
      state.currentCategory = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    addToNotificationQueue: (state, action: PayloadAction<Achievement>) => {
      state.notificationQueue.push(action.payload);
    },
    removeFromNotificationQueue: (state, action: PayloadAction<string>) => {
      state.notificationQueue = state.notificationQueue.filter(
        (achievement) => achievement.id !== action.payload
      );
    },
    clearNotificationQueue: (state) => {
      state.notificationQueue = [];
    },
    markAllAsViewed: (state) => {
      state.achievements = state.achievements.map((achievement) => ({
        ...achievement,
        isNew: false
      }));
      state.newAchievements = [];
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch user achievements
      .addCase(fetchUserAchievements.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserAchievements.fulfilled, (state, action) => {
        state.isLoading = false;
        state.achievements = action.payload;
        state.newAchievements = action.payload.filter(a => a.isNew);
      })
      .addCase(fetchUserAchievements.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch achievements';
      })
      
      // Fetch recent achievements
      .addCase(fetchRecentAchievements.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRecentAchievements.fulfilled, (state, action) => {
        state.isLoading = false;
        state.recentAchievements = action.payload;
      })
      .addCase(fetchRecentAchievements.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch recent achievements';
      })
      
      // Fetch achievements by category
      .addCase(fetchAchievementsByCategory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAchievementsByCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCategory = action.payload.category;
        // Update achievements for the specific category
        const categoryAchievements = action.payload.achievements;
        // Merge with existing achievements
        const otherAchievements = state.achievements.filter(
          a => a.category !== action.payload.category
        );
        state.achievements = [...otherAchievements, ...categoryAchievements];
      })
      .addCase(fetchAchievementsByCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch achievements by category';
      })
      
      // Fetch achievement statistics
      .addCase(fetchAchievementStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAchievementStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchAchievementStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch achievement statistics';
      })
      
      // Mark achievement as viewed
      .addCase(markAchievementAsViewed.fulfilled, (state, action) => {
        const achievementId = action.payload;
        // Update in all arrays
        state.achievements = state.achievements.map(a => 
          a.id === achievementId ? { ...a, isNew: false } : a
        );
        state.recentAchievements = state.recentAchievements.map(a => 
          a.id === achievementId ? { ...a, isNew: false } : a
        );
        state.newAchievements = state.newAchievements.filter(a => a.id !== achievementId);
      })
      
      // Check for new achievements
      .addCase(checkForNewAchievements.pending, (state) => {
        state.error = null;
      })
      .addCase(checkForNewAchievements.fulfilled, (state, action) => {
        const newAchievements = action.payload;
        if (newAchievements.length > 0) {
          // Add to achievements list
          state.achievements = [...state.achievements, ...newAchievements];
          // Add to new achievements
          state.newAchievements = [...state.newAchievements, ...newAchievements];
          // Add to notification queue
          state.notificationQueue = [...state.notificationQueue, ...newAchievements];
        }
        state.lastChecked = Date.now();
      })
      .addCase(checkForNewAchievements.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to check for new achievements';
      });
  }
});

export const {
  setCurrentCategory,
  clearError,
  addToNotificationQueue,
  removeFromNotificationQueue,
  clearNotificationQueue,
  markAllAsViewed
} = achievementsSlice.actions;

// Selectors
export const selectAllAchievements = (state: { achievements: AchievementsState }) => 
  state.achievements.achievements;

export const selectRecentAchievements = (state: { achievements: AchievementsState }) => 
  state.achievements.recentAchievements;

export const selectNewAchievements = (state: { achievements: AchievementsState }) => 
  state.achievements.newAchievements;

export const selectAchievementsByCategory = (category: string) => 
  (state: { achievements: AchievementsState }) => 
    state.achievements.achievements.filter(a => a.category === category);

export const selectAchievementStats = (state: { achievements: AchievementsState }) => 
  state.achievements.statistics;

export const selectNotificationQueue = (state: { achievements: AchievementsState }) => 
  state.achievements.notificationQueue;

export const selectIsLoading = (state: { achievements: AchievementsState }) => 
  state.achievements.isLoading;

export const selectError = (state: { achievements: AchievementsState }) => 
  state.achievements.error;

export default achievementsSlice.reducer;